import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PricingManagementModal = ({ package: pkg, onClose, onSave }) => {
  const [regionalPricing, setRegionalPricing] = useState([]);
  const [newCountry, setNewCountry] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newCurrency, setNewCurrency] = useState('EUR');

  useEffect(() => {
    if (pkg?.regionalPricing) {
      setRegionalPricing(pkg?.regionalPricing);
    }
  }, [pkg]);

  const currencyOptions = [
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'CHF', label: 'CHF (Fr)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'USD', label: 'USD ($)' }
  ];

  const formatAmount = (value, currency) => {
    const currencyMap = {
      'EUR': 'EUR',
      'CHF': 'CHF',
      'GBP': 'GBP',
      'USD': 'USD'
    };
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyMap?.[currency] || 'EUR',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const handleAddRegion = () => {
    if (newCountry?.trim() && newPrice) {
      setRegionalPricing([
        ...regionalPricing,
        {
          country: newCountry?.trim(),
          price: parseFloat(newPrice),
          currency: newCurrency
        }
      ]);
      setNewCountry('');
      setNewPrice('');
      setNewCurrency('EUR');
    }
  };

  const handleRemoveRegion = (index) => {
    setRegionalPricing(regionalPricing?.filter((_, i) => i !== index));
  };

  const handleUpdatePrice = (index, newPrice) => {
    const updated = [...regionalPricing];
    updated[index] = { ...updated?.[index], price: parseFloat(newPrice) };
    setRegionalPricing(updated);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(regionalPricing);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-slide-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Gestion des Tarifs</h2>
            <p className="text-sm text-muted-foreground mt-1">{pkg?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={18} className="text-primary" />
                <h3 className="font-semibold text-foreground">Prix de base</h3>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {formatAmount(pkg?.basePrice, 'EUR')}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Tarification régionale</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Input
                  value={newCountry}
                  onChange={(e) => setNewCountry(e?.target?.value)}
                  placeholder="Pays"
                />
                <Input
                  type="number"
                  step="0.01"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e?.target?.value)}
                  placeholder="Prix"
                />
                <div className="flex gap-2">
                  <Select
                    options={currencyOptions}
                    value={newCurrency}
                    onChange={(value) => setNewCurrency(value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    iconName="Plus"
                    onClick={handleAddRegion}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {regionalPricing?.map((region, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="MapPin" size={16} className="text-primary" />
                        <span className="font-semibold text-foreground">{region?.country}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          step="0.01"
                          value={region?.price}
                          onChange={(e) => handleUpdatePrice(index, e?.target?.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">{region?.currency}</span>
                        <span className="text-lg font-semibold text-foreground">
                          {formatAmount(region?.price, region?.currency)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRegion(index)}
                      className="text-error hover:text-error/80 transition-colors"
                    >
                      <Icon name="Trash2" size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-warning mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Note importante</h4>
                  <p className="text-sm text-muted-foreground">
                    Les modifications de tarifs s'appliqueront uniquement aux nouvelles ventes. Les commandes en cours conserveront leurs prix d'origine.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
            >
              Enregistrer les tarifs
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingManagementModal;